import { useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

/**
 * Personalización por usuario de las tablas de reportes (21-oct-2026):
 * anchos de columna (arrastre) y colores de celda/fila/columna (click
 * derecho). Se guardan en el perfil del usuario (user_ui_prefs) — solo él
 * los ve. Guardado con debounce para no saturar el API.
 */

export interface TablaPrefs {
  /** ancho en px por columna */
  w: Record<string, number>;
  /** color de fila por rowKey */
  rows: Record<string, string>;
  /** color de celda por `${rowKey}|${colKey}` */
  cells: Record<string, string>;
  /** color de columna por colKey */
  cols: Record<string, string>;
}
export const TABLA_PREFS_DEF: TablaPrefs = { w: {}, rows: {}, cells: {}, cols: {} };

export function useTablaPrefs(clave: string) {
  const [prefs, setPrefsState] = useState<TablaPrefs>(TABLA_PREFS_DEF);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    let alive = true;
    api<{ valor: Partial<TablaPrefs> | null }>(`/me/ui-prefs/${clave}`)
      .then((r) => {
        if (alive && r?.valor) setPrefsState({ ...TABLA_PREFS_DEF, ...r.valor });
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [clave]);
  const setPrefs = (up: (prev: TablaPrefs) => TablaPrefs) => {
    setPrefsState((prev) => {
      const next = up(prev);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        api(`/me/ui-prefs/${clave}`, { method: 'PUT', body: JSON.stringify({ valor: next }) }).catch(
          () => {},
        );
      }, 800);
      return next;
    });
  };
  return [prefs, setPrefs] as const;
}

/** Arrastre del borde derecho del th para cambiar el ancho de la columna. */
export function startColResize(
  e: React.MouseEvent,
  key: string,
  setW: (k: string, w: number) => void,
) {
  e.preventDefault();
  e.stopPropagation();
  const th = (e.currentTarget as HTMLElement).closest('th');
  if (!th) return;
  const startX = e.clientX;
  const startW = th.getBoundingClientRect().width;
  const onMove = (me: MouseEvent) =>
    setW(key, Math.max(36, Math.round(startW + me.clientX - startX)));
  const onUp = () => {
    document.removeEventListener('mousemove', onMove);
    document.removeEventListener('mouseup', onUp);
    document.body.style.cursor = '';
  };
  document.body.style.cursor = 'col-resize';
  document.addEventListener('mousemove', onMove);
  document.addEventListener('mouseup', onUp);
}

/** Manija de redimensionado para poner dentro del th (requiere th relative/sticky). */
export function ResizeHandle({
  colKey,
  setW,
}: {
  colKey: string;
  setW: (k: string, w: number) => void;
}) {
  return (
    <span
      className="absolute top-0 right-0 h-full w-2 cursor-col-resize select-none hover:bg-brand-400/60"
      onMouseDown={(e) => startColResize(e, colKey, setW)}
      onClick={(e) => e.stopPropagation()}
      title="Arrastra para cambiar el ancho"
    />
  );
}

export const PALETA = ['#FFF3BF', '#FFD8A8', '#FFC9C9', '#D3F9D8', '#D0EBFF', '#E5DBFF'];

export interface MenuColor {
  x: number;
  y: number;
  rowKey: string;
  colKey: string;
}

/** Menú de colores personales (click derecho en una celda). */
export function ColorMenu({
  menu,
  onPick,
  onClose,
}: {
  menu: MenuColor;
  onPick: (scope: 'cell' | 'row' | 'col', color: string | null) => void;
  onClose: () => void;
}) {
  const fila = (scope: 'cell' | 'row' | 'col', label: string) => (
    <div className="flex items-center gap-1 px-2 py-1">
      <span className="w-16 text-xs text-ink-500">{label}</span>
      {PALETA.map((c) => (
        <button
          key={c}
          className="h-5 w-5 rounded border border-ink-200 hover:scale-110"
          style={{ backgroundColor: c }}
          onClick={() => {
            onPick(scope, c);
            onClose();
          }}
        />
      ))}
      <button
        className={cn('h-5 w-5 rounded border border-ink-300 text-[10px] leading-4 text-ink-500 hover:bg-surface-2')}
        title="Quitar color"
        onClick={() => {
          onPick(scope, null);
          onClose();
        }}
      >
        ✕
      </button>
    </div>
  );
  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} onContextMenu={(e) => { e.preventDefault(); onClose(); }} />
      <div
        className="fixed z-50 rounded-md border border-ink-200 bg-surface-1 shadow-xl py-1"
        style={{ left: Math.min(menu.x, window.innerWidth - 290), top: Math.min(menu.y, window.innerHeight - 120) }}
      >
        <p className="px-2 pb-1 text-[11px] text-ink-400 border-b border-ink-100">
          Color personal (solo tú lo ves)
        </p>
        {fila('cell', 'Celda')}
        {fila('row', 'Fila')}
        {fila('col', 'Columna')}
      </div>
    </>
  );
}

/** Color efectivo de una celda según prefs (celda > fila > columna). */
export function colorPersonal(
  prefs: TablaPrefs,
  rowKey: string,
  colKey: string,
): string | undefined {
  return prefs.cells[`${rowKey}|${colKey}`] ?? prefs.rows[rowKey] ?? prefs.cols[colKey];
}

/** Aplica un pick del menú a las prefs. */
export function aplicarColor(
  prefs: TablaPrefs,
  menu: MenuColor,
  scope: 'cell' | 'row' | 'col',
  color: string | null,
): TablaPrefs {
  const next = { ...prefs, rows: { ...prefs.rows }, cells: { ...prefs.cells }, cols: { ...prefs.cols } };
  const key = scope === 'cell' ? `${menu.rowKey}|${menu.colKey}` : scope === 'row' ? menu.rowKey : menu.colKey;
  const mapa = scope === 'cell' ? next.cells : scope === 'row' ? next.rows : next.cols;
  if (color) mapa[key] = color;
  else delete mapa[key];
  return next;
}
